import { Link } from 'react-router-dom'
import { Avatar, Stack, Typography, Grid, IconButton } from '@mui/material';
import { song_title, image_cover } from './Player.module.css';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import withMK from '../../hoc/withMK';
import { artworkForMediaItem } from '../../utils/Utils';
import { pause, play, skipToNext, skipToPrevious } from '../../services/MusicPlayerApi';
import { withQueueModal, QueueModalProps } from '../../core/providers/QueueProvider';
import { ModalProviderValue, withModal } from '../../core/providers/ModalProvider';
import AppleMusicVolume from './components/appleMusicVolume';
import AppleMusicProgress from './components/appleMusicProgress';
import { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { ComposersNames } from './components/ComposersNames';
import { InterpretorsNames } from './components/InterpretorsNames';
import { useGetTrackByAppleIdQuery } from '../../api/trackApiSlice';

interface PlayerProps extends MKProps, QueueModalProps {
    modal: ModalProviderValue,
}

const Player = (props: PlayerProps) => {
    const [hidePlayer, setHidePlayer] = useState<boolean>(true)
    const playing = props.mk && props.mk.instance ? props.mk.instance.isPlaying : false
    const nowPlayingItem = props.mk && props.mk.instance ? props.mk.instance.queue.currentItem : undefined
    const artworkURL = artworkForMediaItem(nowPlayingItem, 600)

    const { 
        data: track, 
    } = useGetTrackByAppleIdQuery(props.mk && props.mk.instance && props.mk.instance.queue.currentItem && props.mk.instance.queue.currentItem.id != null ? props.mk.instance.queue.currentItem.id : '0')  
    
    let trackColor = track && track.apple_artwork_bg ? '#'+track.apple_artwork_bg : '#ffffff'
    let artworkColor = track && track.apple_artwork_color1 ? '#'+track.apple_artwork_color1 : '#5151a0'

    return ( 
        <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} sx={{minHeight: '5vh'}}>
            { nowPlayingItem && !hidePlayer ?
            <Grid container spacing={2} alignItems={'center'} sx={{ 
                    backgroundColor: artworkColor, 
                    borderRadius: '16px', 
                    p: 1,
                    width: {xs: '80vw',md: '520px',lg: '520px'},
                    opacity: {xs: '1',md: '1',lg: '1'}, 
                    height: {xs: '88px',md: '180px',lg: '180px'}, 
                    overflow: 'visible', 
                    bottom: {xs: '10vh',md: '10vh',lg: '16px'}, 
                    right: {xs: '16px',md: '16px',lg: 'inherit'}, 
                    left: {xs: 'inherit',md: 'inherit',lg: '16px'}, 
                    position: {xs: 'fixed',md: 'fixed',lg: 'fixed'}, 
                    zIndex: 1000
                }}>

                <IconButton onClick={() => setHidePlayer(true)} sx={{display: {xs: 'inline', md: 'inline', lg: 'inline'}, position: 'absolute', bottom: {xs: '-24px', lg: '-16px'}, top: {lg: '8px'}, right: {xs: '30%', lg: '8px'}, width: '36px', height: '36px', backgroundColor: artworkColor}}>
                    <CloseIcon sx={{color: trackColor, fontSize: {xs: '1.2rem', md: '2vmin', lg: '2vmin'},}} />
                </IconButton>

                <Grid size={{xs: 3, sm:2, md: 4, lg: 4}} alignSelf={'start'} sx={{height: '100%'}} >
                    { artworkURL || track
                        ? <img src={artworkURL ? artworkURL : track.apple_artwork_url.replace('{w}', '600').replace('{h}', '600')} className='image-fit-height' alt="placeholder cover"/>
                        : <Avatar variant='square' sx={image_cover} alt="placeholder cover"><MusicNoteIcon/></Avatar>
                    }
                </Grid>

                <Grid size={{xs: 9, sm:10, md: 8, lg: 8}}>
                    <Grid container spacing={0}>
                        <Grid size={{xs: 10, md: 12, lg:12}}>
                            <Stack direction={'column'} spacing={0} sx={{ mb: 0, width: '100%' }} alignItems="left">
                                <Typography sx={{...song_title, ...{textAlign: 'left', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden', color: trackColor}}} >{
                                    track && track.apple_title
                                    ? <Link to={`/track/${track._id}`} style={{textDecoration: 'none', color: trackColor}}>{track.apple_title}</Link>
                                    : nowPlayingItem.attributes.name
                                }</Typography>
                                
                                <InterpretorsNames track={track} defaultNames={nowPlayingItem.attributes.artitsName} />

                                <Stack spacing={0} direction="row" sx={{display: {xs: 'none', md: 'flex', lg: 'flex'}, height: '1vh', minWidth: '200px', pl:1.2, pr:1.2, pt: 2.3, pb: 1, mb: 1, '& apple-music-progress' :{ width: '100%', }, borderRadius: '24px', backgroundColor: trackColor }} alignItems="center" >
                                    <AppleMusicProgress theme={track && track.apple_artwork_bg && isLight(track.apple_artwork_bg) ? 'dark' : 'light'} />
                                </Stack>

                                <ComposersNames track={track} defaultNames={nowPlayingItem.attributes.composersName} clear={false} />

                            </Stack>
                        </Grid>

                        <Grid size={{xs:0, md:6, lg:6}} alignContent={'center'} sx={{display: {xs: 'none', md: 'flex', lg: 'flex'}}}>
                            <Stack spacing={2} direction="row" alignContent={'center'} alignItems={'center'} alignSelf={'center'} sx={{'& apple-music-volume' :{ width: '100%', borderRadius: '16px', p: 0.8, backgroundColor: trackColor}}}>
                                <AppleMusicVolume theme={track && track.apple_artwork_bg && isLight(track.apple_artwork_bg) ? 'dark' : 'light'} />
                            </Stack>
                        </Grid>   

                        <Grid size={{xs:2, md:4, lg:4}}>
                            <Stack direction={{xs: 'column', md: 'row', lg: 'row'}} alignSelf={'center'}>
                                <IconButton sx={{minWidth: '1px', display: {xs: 'none', md: 'inline-flex', lg: 'inline-flex'}}} onClick={() => skipToPrevious()}><SkipPreviousIcon htmlColor={ trackColor} sx={{fontSize: {xs: '1rem', md: '1.8rem', lg: '2vmin'}, p:0 }}/></IconButton>
                                <IconButton sx={{minWidth: '1px'}} onClick={playing ? () => pause() : () => play()}>
                                {
                                    playing
                                    ? <PauseIcon htmlColor={ trackColor} sx={{fontSize: {xs: '1.4rem', md: '1.8rem', lg: '2vmin'}, p:0 }}/>
                                    : <PlayArrowIcon htmlColor={ trackColor} sx={{fontSize: {xs: '1.4rem', md: '1.8rem', lg: '2vmin'}, p:0 }}/>
                                }
                                </IconButton>
                                <IconButton sx={{minWidth: '1px'}} onClick={() => skipToNext()}><SkipNextIcon htmlColor={ trackColor} sx={{fontSize: {xs: '1.4rem', md: '1.8rem', lg: '2vmin'}, p:0 }}/></IconButton>
                            </Stack>
                        </Grid>

                    </Grid>           
                </Grid>           
            </Grid> : null}
            { nowPlayingItem ? 
                <Typography onClick={() => setHidePlayer(!hidePlayer)} sx={{...song_title, ...{textAlign: 'left', textOverflow: 'ellipsis', overflow: 'hidden', color: '#ffffff', pl: 2}}} >
                    {
                        track && track.apple_title_clear
                        ? track.apple_title_clear
                        : nowPlayingItem.attributes.name
                    }
                </Typography>
            : '' }
            { nowPlayingItem ? <IconButton sx={{minWidth: '1px', p: 2}} onClick={playing ? () => pause() : () => play()}>
            {
                playing
                ? <PauseIcon htmlColor={'#ffffff'} sx={{fontSize: {xs: '1.4rem', md: '1.8rem', lg: '2vmin'}, p:0 }}/>
                : <PlayArrowIcon htmlColor={'#ffffff'} sx={{fontSize: {xs: '1.4rem', md: '1.8rem', lg: '2vmin'}, p:0 }}/>
            }
            </IconButton> : null }
        </Stack> 
    )
}

const bindings = {
    [MusicKit.Events.mediaItemDidChange]: 'mediaItem',
    [MusicKit.Events.queueItemsDidChange]: 'queueItems',
    [MusicKit.Events.queuePositionDidChange]: 'queuePosition',
    [MusicKit.Events.playbackStateDidChange]: 'playbackState',
}

export default withMK(
    withQueueModal(withModal( Player )), 
    bindings
)

const isLight = (colorHex: string) => {
    var c: any = colorHex.substring(1);      // strip #
    var rgb = parseInt(c, 16);   // convert rrggbb to decimal
    var r = (rgb >> 16) & 0xff;  // extract red
    var g = (rgb >>  8) & 0xff;  // extract green
    var b = (rgb >>  0) & 0xff;  // extract blue
    
    var luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709
    
    if (luma < 40) {
       return true
    }
}